import type { Clock, Scheduler } from "./pricing-controller";

/* ============================================================
   Types
   ============================================================ */

export type ArtworkMode = "upload-now" | "send-later" | "request-upload-link" | "need-design";

export type UploadRejectionCode =
  | "UNSUPPORTED_FILE_TYPE" | "FILE_TOO_LARGE" | "CORRUPT_FILE"
  | "FILE_SIGNATURE_MISMATCH" | "FILE_VALIDATION_FAILED" | "PROCESSING_FAILED";

export type AcceptedPrintFile = { id: string; filename: string };

export type ArtworkWarning = {
  code: "LOW_RESOLUTION" | "ASPECT_RATIO_MISMATCH" | "BLEED_MISSING";
  message: string;
  blocking: false;
};

export type ArtworkAssessmentState =
  | { status: "idle"; warnings: [] }
  | { status: "checking"; warnings: [] }
  | { status: "ready"; warnings: ArtworkWarning[] }
  | { status: "error"; warnings: [] };

export type UploadSessionStatus =
  | "pending" | "polling" | "manual-check" | "processing" | "rejected" | "expired" | "failed";

export type UploadSessionState = {
  id: string;
  expiresAt: string;
  status: UploadSessionStatus;
  /** Runtime-only capability URL. Never serialized into the draft. */
  uploadUrl: string | null;
};

export type DirectUploadState =
  | { status: "idle" }
  | { status: "uploading"; progress: number | null }
  | { status: "processing" }
  | { status: "rejected"; code: UploadRejectionCode }
  | { status: "error"; code: string };

export type ArtworkState = {
  mode: ArtworkMode | null;
  file: AcceptedPrintFile | null;
  directUpload: DirectUploadState;
  uploadSession: UploadSessionState | null;
  assessment: ArtworkAssessmentState;
};

export type ArtworkOrderReadiness =
  | { ready: true; selection: { artworkMode: ArtworkMode; artworkFileId: string | null } }
  | { ready: false; reason: "MODE_REQUIRED" | "FILE_REQUIRED" | "UPLOAD_PROCESSING" };

export type ArtworkModeChangeResult =
  | { type: "changed" }
  | { type: "confirmation-required"; reason: "DETACH_ACCEPTED_FILE" | "CANCEL_UPLOAD_SESSION"; requestedMode: ArtworkMode }
  | { type: "rejected"; code: "MODE_UNAVAILABLE" };

export type ArtworkTarget = { widthCm: number; heightCm: number };

export type ArtworkDraftSnapshot = {
  artworkMode: ArtworkMode | null;
  artworkFileId: string | null;
  uploadSessionId: string | null;
};

export type UploadSessionSnapshot =
  | { status: "pending" | "processing"; expiresAt: string }
  | { status: "accepted"; expiresAt: string; file: AcceptedPrintFile }
  | { status: "rejected"; expiresAt: string; rejectionCode: UploadRejectionCode }
  | { status: "expired"; expiresAt: string }
  | { status: "failed"; expiresAt: string };

export interface ArtworkGateway {
  uploadFile(
    file: File,
    options: { signal: AbortSignal; onProgress?: (value: number) => void }
  ): Promise<{ type: "accepted"; file: AcceptedPrintFile } | { type: "rejected"; code: UploadRejectionCode }>;
  verifyPrintFile(printFileId: string): Promise<{ valid: true; file: AcceptedPrintFile } | { valid: false }>;
  removePrintFile(printFileId: string): Promise<void>;
  createUploadSession(): Promise<{ id: string; uploadUrl: string; expiresAt: string }>;
  getUploadSessionStatus(id: string): Promise<UploadSessionSnapshot>;
  cancelUploadSession(id: string): Promise<void>;
  assessArtwork(input: { printFileId: string; widthCm: number; heightCm: number }): Promise<ArtworkWarning[]>;
}

/** Ambient signals the controller must not read from globals directly. */
export interface ArtworkEnvironment {
  isVisible(): boolean;
  isOnline(): boolean;
  onVisibilityChange(listener: () => void): () => void;
}

export interface ArtworkController {
  getState(): ArtworkState;
  setMode(mode: ArtworkMode): Promise<ArtworkModeChangeResult>;
  confirmModeChange(mode: ArtworkMode): Promise<void>;
  uploadNow(file: File): Promise<void>;
  cancelDirectUpload(): void;
  createUploadLink(): Promise<void>;
  checkUploadSession(): Promise<void>;
  removeAcceptedFile(): Promise<void>;
  /** Clears a file the server told us is no longer usable; no delete request is sent. */
  invalidateAcceptedFile(): void;
  setAssessmentTarget(target: ArtworkTarget | null): void;
  restoreDraft(draft: ArtworkDraftSnapshot): Promise<void>;
  getOrderReadiness(): ArtworkOrderReadiness;
  getDraftSnapshot(): ArtworkDraftSnapshot;
  subscribe(listener: (state: ArtworkState) => void): () => void;
  dispose(): void;
}

const defaultScheduler: Scheduler = {
  setTimeout(cb, ms) { return globalThis.setTimeout(cb, ms); },
  clearTimeout(id) { globalThis.clearTimeout(id as ReturnType<typeof setTimeout>); },
};

const defaultClock: Clock = { now: () => Date.now() };

const MODES_NEEDING_FILE: ArtworkMode[] = ["upload-now", "request-upload-link"];

/* ============================================================
   Controller
   ============================================================ */

export function createArtworkController(options: {
  gateway: ArtworkGateway;
  environment: ArtworkEnvironment;
  scheduler?: Scheduler;
  clock?: Clock;
  availableModes?: ArtworkMode[];
  pollIntervalMs?: number;
  pollBudgetMs?: number;
}): ArtworkController {
  const gateway = options.gateway;
  const env = options.environment;
  const scheduler = options.scheduler ?? defaultScheduler;
  const clock = options.clock ?? defaultClock;
  const availableModes = options.availableModes ?? ["upload-now", "send-later", "request-upload-link", "need-design"];
  const pollIntervalMs = options.pollIntervalMs ?? 5_000;
  const pollBudgetMs = options.pollBudgetMs ?? 30 * 60_000;

  let state: ArtworkState = {
    mode: null,
    file: null,
    directUpload: { status: "idle" },
    uploadSession: null,
    assessment: { status: "idle", warnings: [] },
  };

  let disposed = false;
  const listeners = new Set<(s: ArtworkState) => void>();

  /** Guards every file-producing operation: upload, verify, remove. */
  let fileOperationRevision = 0;
  /** Guards everything belonging to one upload link. */
  let uploadSessionRevision = 0;
  /** Guards assessment responses against dimension changes. */
  let assessmentRevision = 0;

  let uploadAbort: AbortController | null = null;
  let pollTimer: unknown | null = null;
  let pollBudgetUsedMs = 0;
  let target: ArtworkTarget | null = null;

  const unsubscribeVisibility = env.onVisibilityChange(() => {
    if (disposed) return;
    if (env.isVisible()) void resumePollingAfterVisible();
    else stopPollTimer();
  });

  function emit() { if (!disposed) for (const l of listeners) l(state); }
  function setState(patch: Partial<ArtworkState>) { state = { ...state, ...patch }; emit(); }

  function stopPollTimer() {
    if (pollTimer !== null) { scheduler.clearTimeout(pollTimer); pollTimer = null; }
  }

  /* ---------- mode ---------- */

  function modeRequiresConfirmation(next: ArtworkMode): ArtworkModeChangeResult | null {
    if (state.file && next !== state.mode) {
      return { type: "confirmation-required", reason: "DETACH_ACCEPTED_FILE", requestedMode: next };
    }
    if (state.uploadSession && next !== "request-upload-link") {
      return { type: "confirmation-required", reason: "CANCEL_UPLOAD_SESSION", requestedMode: next };
    }
    return null;
  }

  async function applyModeChange(mode: ArtworkMode): Promise<void> {
    // Local intent wins immediately. Cancelling the remote upload session is
    // best-effort cleanup and must never block the customer from changing mode.
    fileOperationRevision++;
    uploadAbort?.abort();
    uploadAbort = null;

    const sessionToCancel =
      state.uploadSession && mode !== "request-upload-link"
        ? state.uploadSession.id
        : null;

    if (sessionToCancel) {
      uploadSessionRevision++;
      stopPollTimer();
      pollBudgetUsedMs = 0;
    }

    assessmentRevision++;
    setState({
      mode,
      file: null,
      directUpload: { status: "idle" },
      uploadSession: mode === "request-upload-link" ? state.uploadSession : null,
      assessment: { status: "idle", warnings: [] },
    });

    if (sessionToCancel) {
      try {
        void gateway.cancelUploadSession(sessionToCancel).catch(() => {
          // Best effort only. The superseded server session will expire naturally.
        });
      } catch {
        // A synchronous adapter failure is equally non-blocking.
      }
    }
  }

  async function setMode(mode: ArtworkMode): Promise<ArtworkModeChangeResult> {
    if (disposed) return { type: "rejected", code: "MODE_UNAVAILABLE" };
    if (!availableModes.includes(mode)) return { type: "rejected", code: "MODE_UNAVAILABLE" };
    if (mode === state.mode) return { type: "changed" };

    const confirmation = modeRequiresConfirmation(mode);
    if (confirmation) return confirmation;

    await applyModeChange(mode);
    return { type: "changed" };
  }

  async function confirmModeChange(mode: ArtworkMode): Promise<void> {
    if (disposed) return;
    if (!availableModes.includes(mode)) return;
    await applyModeChange(mode);
  }

  /* ---------- direct upload ---------- */

  async function uploadNow(file: File): Promise<void> {
    if (disposed) return;

    const revision = ++fileOperationRevision;
    uploadAbort?.abort();
    const abort = new AbortController();
    uploadAbort = abort;

    setState({ directUpload: { status: "uploading", progress: 0 } });

    let result: Awaited<ReturnType<ArtworkGateway["uploadFile"]>>;
    try {
      result = await gateway.uploadFile(file, {
        signal: abort.signal,
        onProgress: value => {
          if (disposed || revision !== fileOperationRevision) return;
          setState({ directUpload: { status: "uploading", progress: value } });
        },
      });
    } catch (error) {
      if (disposed || revision !== fileOperationRevision) return;
      setState({ directUpload: { status: "error", code: "UPLOAD_FAILED" } });
      return;
    }

    if (disposed || revision !== fileOperationRevision) return;

    if (result.type === "rejected") {
      // uploaded !== accepted: no file is written.
      setState({ directUpload: { status: "rejected", code: result.code }, file: null });
      return;
    }

    setState({ directUpload: { status: "idle" }, file: result.file });
    // The assessment is a soft, independent concern: acceptance of the file
    // must not wait on it, and readiness never depends on it.
    void runAssessment(result.file.id);
  }

  function cancelDirectUpload(): void {
    if (disposed) return;
    fileOperationRevision++;
    uploadAbort?.abort();
    uploadAbort = null;
    setState({ directUpload: { status: "idle" } });
  }

  async function removeAcceptedFile(): Promise<void> {
    if (disposed || !state.file) return;
    const fileId = state.file.id;
    const revision = ++fileOperationRevision;

    try {
      await gateway.removePrintFile(fileId);
    } catch {
      // A failed server removal must not silently drop the local reference:
      // the customer would think the artwork is gone when it is not.
      return;
    }

    if (disposed || revision !== fileOperationRevision) return;
    assessmentRevision++;
    setState({ file: null, assessment: { status: "idle", warnings: [] } });
  }

  function invalidateAcceptedFile(): void {
    if (disposed) return;
    fileOperationRevision++;
    assessmentRevision++;
    uploadAbort?.abort();
    uploadAbort = null;
    setState({
      file: null,
      directUpload: { status: "idle" },
      assessment: { status: "idle", warnings: [] },
    });
  }

  /* ---------- cross-device session ---------- */

  async function createUploadLink(): Promise<void> {
    if (disposed) return;

    const revision = ++uploadSessionRevision;
    stopPollTimer();
    pollBudgetUsedMs = 0;

    const session = await gateway.createUploadSession();
    if (disposed || revision !== uploadSessionRevision) return;

    setState({ uploadSession: { id: session.id, expiresAt: session.expiresAt, status: "pending", uploadUrl: session.uploadUrl } });
    schedulePoll();
  }

  function schedulePoll() {
    stopPollTimer();
    if (disposed || !state.uploadSession) return;
    if (!env.isVisible() || !env.isOnline()) return;
    if (pollBudgetUsedMs >= pollBudgetMs) {
      setState({ uploadSession: { ...state.uploadSession, status: "manual-check" } });
      return;
    }

    pollTimer = scheduler.setTimeout(() => {
      pollTimer = null;
      pollBudgetUsedMs += pollIntervalMs;
      void pollOnce();
    }, pollIntervalMs);
  }

  async function resumePollingAfterVisible(): Promise<void> {
    if (!state.uploadSession) return;
    if (state.uploadSession.status === "manual-check") return;
    // Immediate check first, then resume the interval.
    await pollOnce();
  }

  async function pollOnce(): Promise<void> {
    if (disposed || !state.uploadSession) return;

    const revision = uploadSessionRevision;
    const sessionId = state.uploadSession.id;

    let snapshot: UploadSessionSnapshot;
    try {
      snapshot = await gateway.getUploadSessionStatus(sessionId);
    } catch {
      if (disposed || revision !== uploadSessionRevision) return;
      schedulePoll();
      return;
    }

    // A snapshot belonging to a superseded link can never write state.
    if (disposed || revision !== uploadSessionRevision) return;
    if (!state.uploadSession || state.uploadSession.id !== sessionId) return;

    applySessionSnapshot(snapshot, sessionId);
  }

  function applySessionSnapshot(snapshot: UploadSessionSnapshot, sessionId: string) {
    const base = { id: sessionId, expiresAt: snapshot.expiresAt, uploadUrl: state.uploadSession?.uploadUrl ?? null };

    switch (snapshot.status) {
      case "accepted":
        stopPollTimer();
        setState({
          uploadSession: null,
          file: snapshot.file,           // filename always comes from the server
          directUpload: { status: "idle" },
        });
        void runAssessment(snapshot.file.id);
        return;

      case "rejected":
        stopPollTimer();
        setState({ uploadSession: { ...base, status: "rejected" }, file: null });
        return;

      case "expired":
        stopPollTimer();
        setState({ uploadSession: { ...base, status: "expired" } });
        return;

      case "failed":
        stopPollTimer();
        setState({ uploadSession: { ...base, status: "failed" } });
        return;

      case "processing":
        setState({ uploadSession: { ...base, status: "processing" } });
        schedulePoll();
        return;

      case "pending":
        setState({ uploadSession: { ...base, status: "polling" } });
        schedulePoll();
        return;
    }
  }

  async function checkUploadSession(): Promise<void> {
    if (disposed || !state.uploadSession) return;
    await pollOnce();
  }

  /* ---------- assessment ---------- */

  function setAssessmentTarget(next: ArtworkTarget | null): void {
    if (disposed) return;
    target = next;
    if (state.file) void runAssessment(state.file.id);
  }

  async function runAssessment(printFileId: string): Promise<void> {
    if (disposed) return;
    if (!target) { setState({ assessment: { status: "idle", warnings: [] } }); return; }

    const revision = ++assessmentRevision;
    const forTarget = target;
    setState({ assessment: { status: "checking", warnings: [] } });

    let warnings: ArtworkWarning[];
    try {
      warnings = await gateway.assessArtwork({ printFileId, widthCm: forTarget.widthCm, heightCm: forTarget.heightCm });
    } catch {
      if (disposed || revision !== assessmentRevision) return;
      setState({ assessment: { status: "error", warnings: [] } });
      return;
    }

    if (disposed || revision !== assessmentRevision) return;
    setState({ assessment: { status: "ready", warnings } });
  }

  /* ---------- draft restore ---------- */

  async function restoreDraft(draft: ArtworkDraftSnapshot): Promise<void> {
    if (disposed) return;

    setState({ mode: draft.artworkMode });

    if (draft.artworkFileId) {
      const revision = ++fileOperationRevision;
      const result = await gateway.verifyPrintFile(draft.artworkFileId);
      if (disposed || revision !== fileOperationRevision) return;

      if (result.valid) {
        setState({ file: result.file, uploadSession: null });
        void runAssessment(result.file.id);
        return;
      }
      // Never show a filename just because sessionStorage held an id.
      setState({ file: null });
    }

    if (draft.uploadSessionId) {
      const revision = ++uploadSessionRevision;
      pollBudgetUsedMs = 0;

      let snapshot: UploadSessionSnapshot;
      try {
        snapshot = await gateway.getUploadSessionStatus(draft.uploadSessionId);
      } catch {
        return;
      }
      if (disposed || revision !== uploadSessionRevision) return;

      state = { ...state, uploadSession: { id: draft.uploadSessionId, expiresAt: snapshot.expiresAt, status: "pending", uploadUrl: null } };
      applySessionSnapshot(snapshot, draft.uploadSessionId);
    }
  }

  /* ---------- selectors ---------- */

  function getOrderReadiness(): ArtworkOrderReadiness {
    const mode = state.mode;
    if (mode === null) return { ready: false, reason: "MODE_REQUIRED" };

    if (state.directUpload.status === "uploading" || state.directUpload.status === "processing") {
      return { ready: false, reason: "UPLOAD_PROCESSING" };
    }

    if (MODES_NEEDING_FILE.includes(mode)) {
      if (!state.file) return { ready: false, reason: "FILE_REQUIRED" };
      return { ready: true, selection: { artworkMode: mode, artworkFileId: state.file.id } };
    }

    // send-later / need-design may carry a file, but do not require one.
    return { ready: true, selection: { artworkMode: mode, artworkFileId: state.file?.id ?? null } };
  }

  function getDraftSnapshot(): ArtworkDraftSnapshot {
    return {
      artworkMode: state.mode,
      artworkFileId: state.file?.id ?? null,
      uploadSessionId: state.uploadSession?.id ?? null,
    };
  }

  return {
    getState: () => state,
    setMode,
    confirmModeChange,
    uploadNow,
    cancelDirectUpload,
    createUploadLink,
    checkUploadSession,
    removeAcceptedFile,
    invalidateAcceptedFile,
    setAssessmentTarget,
    restoreDraft,
    getOrderReadiness,
    getDraftSnapshot,
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    dispose() {
      if (disposed) return;
      disposed = true;
      fileOperationRevision++;
      uploadSessionRevision++;
      assessmentRevision++;
      uploadAbort?.abort();
      uploadAbort = null;
      stopPollTimer();
      unsubscribeVisibility();
      listeners.clear();
    },
  };
}
